// Channels.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { getApiErrorDisplayMessage, twilioSetupIsRegulatoryPending } from "../../../utils/channelSaveError";
import { useChannels } from "../../../hooks/useChannels";
import ChannelTable from "../../../components/ChannelTable";
import AddChannelForm from "../../../components/AddChannelForm";
import ChannelsToolbar from "../../../components/channels/ChannelsToolbar";
import MapChannelsAiDialog from "../../../components/channels/MapChannelsAiDialog";
import { usePageContext } from "../../../context/PageContext";
import { CHANNEL_TYPES } from "../../../constants/channelCatalog";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../../api/client";

const Channels = () => {
  const { t } = useTranslation("settings");
  const autoOpenedRef = useRef(false);
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mapAiOpen, setMapAiOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
  } = useChannels(currentPage, pageSize, search);

  useEffect(() => {
    const list = data?.channels || [];
    const hasSearchFilter = typeof search === "string" && search.trim() !== "";
    const reallyEmpty = !hasSearchFilter && list.length === 0;
    if (!isLoading && !selectedChannel && reallyEmpty && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setSelectedChannel({}); // novo canal
    }
  }, [isLoading, data, selectedChannel, search]);

  useEffect(() => {
    if (error) {
      setErrorMessage("Erro ao carregar canais. Por favor, tente novamente.");
    } else {
      setErrorMessage(null);
    }
  }, [error]);

  /**
   * Tipos cujo formulário depende de campo que a listagem não devolve.
   * Telefonia/PABX precisam do `token` (segredo compartilhado que vai nos
   * exemplos de webhook), e ele só vem em claro no GET por id.
   */
  const TYPES_NEEDING_DETAIL = [
    CHANNEL_TYPES.TWILIO_VIDEO,
    CHANNEL_TYPES.TWILIO_PHONE,
    CHANNEL_TYPES.PABX,
    CHANNEL_TYPES.WHATSAPP_GUPSHUP,
    CHANNEL_TYPES.WHATSAPP_CLOUD,
  ];

  const handleEditChannel = (channel) => {
    setSelectedChannel(channel);
    setErrorMessage(null);
    if (TYPES_NEEDING_DETAIL.includes(Number(channel?.tipo)) && channel?.id) {
      (async () => {
        try {
          const full = await fetchData(`/channels/${channel.id}`, "GET");
          const row =
            full?.data && typeof full.data === "object" && !Array.isArray(full.data) && full.data.id != null
              ? full.data
              : full;
          if (row && typeof row === "object") {
            setSelectedChannel((prev) =>
              prev && Number(prev.id) === Number(channel.id)
                ? { ...prev, ...row }
                : prev
            );
          }
        } catch {
          /* mantém dados da lista */
        }
      })();
    }
  };

  const handleDeleteChannel = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteChannel = () => {
    if (!confirmDelete) return;

    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (error) => {
        setErrorMessage(
          getApiErrorDisplayMessage(error, "channelsPage.deleteChannelError")
        );
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    refetch();
  };

  const handleCloseForm = () => {
    setSelectedChannel(null);
    setErrorMessage(null);
  };

  const handleToggleStatus = (channel) => {
    if (!channel?.id) return;
    const newStatus = Number(channel.status) === 1 ? 0 : 1;
    setIsProcessing(true);
    updateMutation.mutate(
      { ...channel, status: newStatus },
      {
        onSuccess: () => refetch(),
        onError: (error) => {
          setErrorMessage(
            getApiErrorDisplayMessage(error, "channelsPage.toggleStatusError")
          );
        },
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      if (failed.length === ids.length) {
        const firstReason = failed[0]?.reason;
        setErrorMessage(
          getApiErrorDisplayMessage(
            firstReason,
            "channelsPage.deleteChannelsError"
          )
        );
      } else {
        setErrorMessage(
          t("channelsPage.deleteChannelsPartialError", {
            failed: failed.length,
            total: ids.length,
          })
        );
      }
    }
    refetch();
    setIsProcessing(false);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("channelsPage.headingChannels")}
        </Typography>
        {!selectedChannel && (
          <ChannelsToolbar
            onCreateNew={() => setSelectedChannel({})}
            onMapWithAi={() => setMapAiOpen(true)}
          />
        )}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="error"
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: "pre-line" }}
            >
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      )}

      {selectedChannel ? (
        <AddChannelForm
          key={selectedChannel?.id != null ? String(selectedChannel.id) : "new-channel"}
          channel={selectedChannel}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onPersisted={(saved) => {
            setSelectedChannel(saved);
            refetch();
          }}
          onSubmit={async (updatedChannel) => {
            setIsProcessing(true);
            setErrorMessage(null);
            try {
              const result = await updateMutation.mutateAsync(updatedChannel);
              const createdId = result?.data?.id ?? result?.id;
              const lobbyToken =
                result?.video_lobby_token ?? result?.data?.video_lobby_token ?? null;
              const lobbyUrl =
                result?.videoLobbyUrl ?? result?.data?.videoLobbyUrl ?? null;

              const mergeVideoLobby = (base) => ({
                ...base,
                ...(lobbyToken ? { video_lobby_token: lobbyToken } : {}),
                ...(lobbyUrl ? { videoLobbyUrl: lobbyUrl } : {}),
              });

              if (Number(updatedChannel.tipo) === CHANNEL_TYPES.TWILIO_VIDEO) {
                const id = updatedChannel.id || createdId;
                setSelectedChannel(
                  mergeVideoLobby({
                    ...updatedChannel,
                    ...(id ? { id } : {}),
                  })
                );
                refetch();
                return result;
              }

              if (!updatedChannel.id && createdId) {
                if (
                  Number(updatedChannel.tipo) === CHANNEL_TYPES.EMAIL_AWS_SES &&
                  updatedChannel.email_domain
                ) {
                  setSelectedChannel({
                    ...updatedChannel,
                    id: createdId,
                  });
                  refetch();
                  return result;
                }

                if (Number(updatedChannel.tipo) === CHANNEL_TYPES.WHATSAPP_QR) {
                  setSelectedChannel({
                    ...updatedChannel,
                    id: createdId,
                  });
                  refetch();
                  return result;
                }

                if (twilioSetupIsRegulatoryPending(result)) {
                  setSelectedChannel({
                    ...updatedChannel,
                    id: createdId,
                    twilio_phone_number: "",
                    _voicePurchasePending: true,
                  });
                  refetch();
                  return result;
                }

                if (
                  Number(updatedChannel.tipo) === CHANNEL_TYPES.WHATSAPP_GUPSHUP &&
                  String(updatedChannel.whatsapp_calling_mode || "").trim().toLowerCase() ===
                    "gupshup_sip"
                ) {
                  setSelectedChannel({
                    ...updatedChannel,
                    id: createdId,
                  });
                  refetch();
                  return result;
                }

                if (Number(updatedChannel.tipo) === CHANNEL_TYPES.SITE_WEBCHAT) {
                  return result;
                }

                queueMicrotask(() => {
                  refetch();
                  handleCloseForm();
                });
                return result;
              }

              queueMicrotask(() => {
                refetch();
                handleCloseForm();
              });
              return result;
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      ) : (
        <ChannelTable
          channels={data?.channels || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditChannel={handleEditChannel}
          onDeleteChannel={handleDeleteChannel}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <MapChannelsAiDialog
        open={mapAiOpen}
        onClose={() => setMapAiOpen(false)}
        onCreateChannel={(draft) => {
          setMapAiOpen(false);
          setSelectedChannel(draft || {});
        }}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? "Processando exclusão..."
              : "Tem certeza de que deseja excluir este canal?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteChannel}>
                Excluir
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Channels;
